import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    TextField,
} from '@mui/material';
import { Add, DeleteOutline, DeleteOutlined, EditOutlined, ViewAgendaOutlined } from '@mui/icons-material';
import moment from 'moment';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { deleteCategory, getAllCategories, getAllSubcategories, getFaqs, getRoles, removeRole, saveCategory } from '../../../redux/api/userMgt';
import { Form, Formik } from 'formik';
import TextInput from '../../../components/common/TextInput';

const Categories = () => {
    const defCategory = { name: '', subCategoryCodes: [] };
    const [processing, setProcessing] = useState(false);
    const [actionModal, setActionModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(defCategory);
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [categoryFilterValue, setCategoryFilterValue] = useState('');
    const [mapping, setMapping] = useState(false);
    const [mappingModal, setMappingModal] = useState(false);

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.categoryId) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Quick Action',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <IconButton
                            size="small"
                            color={'info'}
                            onClick={() => {
                                setSelectedCategory(params?.row || defCategory);
                                setActionModal(true);
                            }}
                            variant="outlined"
                            title={'Edit Category'}
                        >
                            <EditOutlined />
                        </IconButton>

                        <IconButton
                            size="small"
                            color={'primary'}
                            onClick={() => {
                                setSelectedCategory(params?.row || defCategory);
                                setMappingModal(true);
                            }}
                            variant="outlined"
                            title={'View Sub Categories'}
                        >
                            <ViewAgendaOutlined />
                        </IconButton>

                        <IconButton
                            size="small"
                            color={'error'}
                            onClick={() => {
                                setSelectedCategory(params?.row || defCategory);
                                setDeleteModal(true);
                            }}
                            variant="outlined"
                            title={'Delete Category'}
                        >
                            <DeleteOutline />
                        </IconButton>
                    </div>
                );
            },
        },
        { field: 'name', headerName: 'Name', width: 350 },
        {
            field: 'subCategories',
            headerName: 'Sub Categories',
            width: 150,
            valueGetter: ({ row }) => row?.subCategories?.length || 0,
        },
        { field: 'isActive', headerName: 'Active', width: 150 },
        { field: 'createdBy', headerName: 'Created By', width: 350 },
        // { field: 'createdBy', headerName: 'Created By', width: 200 },
        {
            field: 'createdOn',
            headerName: 'Creation Date',
            width: 200,
            valueGetter: (params) => (params?.row?.createdOn ? moment(params?.row?.createdOn).format('DD MMMM, YYYY') : ''),
        },
        // { field: 'createdTime', headerName: 'Creation Time', width: 200, valueGetter: (params) => (params?.row?.createdDate) ? (moment(params?.row?.createdDate).format('hh:mm:ss A')) : '' }
    ];

    const init = async () => {
        try {
            setProcessing(true);
            const res = await getAllCategories();
            const resp = await getAllSubcategories();
            setRecords(res?.resultObject || []);
            setSubCategories(resp?.resultObject || []);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const remove = async (values) => {
        try {
            const resp = await deleteCategory(values.categoryId || '');
            if (resp?.isSuccess) {
                setDeleteModal(false);
                setSelectedCategory(defCategory);
                enqueueSnackbar('Category Deleted Successfully');
                init();
            } else {
                enqueueSnackbar(resp?.message || `An error occurred`, {
                    variant: 'error',
                });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const create = async (values) => {
        try {
            const resp = await saveCategory({ ...values });
            if (resp?.isSuccess) {
                setSelectedCategory(defCategory);
                enqueueSnackbar(`Category ${values?.categoryId ? 'Edited' : 'Created'} Successfully`);
                setActionModal(false);
                init();
            } else {
                enqueueSnackbar(resp?.message || `An error occurred`, {
                    variant: 'error',
                });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const handleChange = ({ target }, value) => {
        let subCategories = [...selectedCategory?.subCategories] || [];
        if (value) {
            subCategories.push({ code: target.id });
        } else {
            subCategories = subCategories.filter(({ code }) => code != target.id);
        }
        setSelectedCategory({ ...selectedCategory, subCategories });
    };

    const hasPermission = (code) => {
        return selectedCategory?.subCategories?.map((x) => x?.code)?.indexOf(code) >= 0 ? true : false;
    };

    const handleMapping = async () => {
        try {
            setMapping(true);

            const resp = await saveCategory({
                ...selectedCategory,
                subCategorieCodes: selectedCategory?.subCategories?.map(({ code }) => code) || [],
            });
            if (resp?.isSuccess) {
                enqueueSnackbar(`Sub categories Mapped successfully`);
                init();
                setMappingModal(false);
                setSelectedCategory(defCategory);
            } else {
                enqueueSnackbar(resp?.message || `An error occurred`, {
                    variant: 'error',
                });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setMapping(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer
            pageTitle="All Categories"
            tagline="Manage all categories"
            processing={processing}
            buttonComponent={
                <>
                    <Button onClick={() => setActionModal(true)} startIcon={<Add />} variant="contained">
                        Create Category
                    </Button>
                </>
            }
        >
            <Box>
                <div className="paper" style={{ height: 550, width: '100%', textAlign: 'center' }}>
                    <DataGrid
                        className={'center-first-column'}
                        rows={records}
                        getRowId={(row) => row.categoryId}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        style={{ fontSize: '12px' }}
                        disableSelectionOnClick
                    />
                </div>
            </Box>

            <Dialog open={actionModal} maxWidth="lg" PaperProps={{ style: { minWidth: '50vw' } }}>
                <Formik initialValues={{ ...selectedCategory }} enableReinitialize onSubmit={create}>
                    {({ isSubmitting, values }) => (
                        <Form>
                            <DialogContent>
                                <h4 className="mb-1">{selectedCategory?.categoryId ? 'Edit' : 'Create'} Category</h4>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextInput id="name" required label="Name" />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions className="pb-1 pr-1">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => {
                                        setActionModal(false);
                                        setSelectedCategory(defCategory);
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button variant="contained" color={'primary'} type="submit" disabled={isSubmitting}>
                                    {Boolean(isSubmitting) ? <CircularProgress color="inherit" size={15} /> : selectedCategory?.categoryId ? 'Edit' : 'Create'}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Dialog open={Boolean(deleteModal)}>
                <h4 className="text-center mt-2 mb-0">Confirm Category Deletion</h4>
                <Formik initialValues={{ ...selectedCategory }} enableReinitialize onSubmit={remove}>
                    {({ isSubmitting, values }) => (
                        <Form>
                            <DialogContent>
                                <Grid container spacing={3} paddingBottom={3}>
                                    <Grid item xs={12}>
                                        Kindly confirm you want to delete category with details below:
                                        <br />
                                        <br />
                                        <b>Name:</b> {values?.name} <br />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Button
                                            disabled={isSubmitting}
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => {
                                                setDeleteModal(false);
                                                setSelectedCategory(defCategory);
                                            }}
                                            style={{ marginTop: '.5rem' }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button disabled={isSubmitting} startIcon={<DeleteOutlined />} color="error" fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                            {isSubmitting ? <CircularProgress size={15} color="inherit" /> : 'Delete Category'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Dialog open={mappingModal} maxWidth="lg">
                <DialogTitle className="no-margin m-0 pt-2" style={{ textTransform: 'initial' }}>
                    <span className="m-0 mb-1 d-block">
                        Map Sub-Catgories to Team: <b>{selectedCategory?.name}</b>
                    </span>
                    <TextField
                        value={categoryFilterValue}
                        onChange={(ev) => setCategoryFilterValue(ev?.target?.value || '')}
                        fullWidth
                        placeholder="Search..."
                        inputProps={{
                            style: { padding: '11px 14px', fontSize: '14px' },
                        }}
                        className="mb-0"
                    />
                </DialogTitle>
                <DialogContent
                    style={{
                        height: '500px',
                        width: '600px',
                        padding: '0.5rem 0 0 2rem',
                    }}
                >
                    <FormControl>
                        <FormGroup>
                            {subCategories
                                ?.filter(({ name, categoryName }) => {
                                    if (categoryFilterValue) {
                                        const value = String(categoryFilterValue).toLowerCase();
                                        name = String(name).toLowerCase();
                                        categoryName = String(categoryName).toLowerCase();

                                        return Boolean(name?.includes(value) || categoryName?.includes(value));
                                    } else {
                                        return true;
                                    }
                                })
                                ?.map((category, key) => (
                                    <>
                                        <FormControlLabel
                                            control={<Checkbox checked={hasPermission(category.code)} id={category.code} onChange={handleChange} name={category.name} />}
                                            label={
                                                <p className="m-0">
                                                    {category?.name || ''}
                                                    <br />
                                                    <small>Category: {category?.categoryName || ''}</small>
                                                </p>
                                            }
                                            key={key}
                                        />
                                        <Divider className="mb-05" />
                                    </>
                                ))}
                        </FormGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions style={{ padding: '1rem' }}>
                    <Button
                        onClick={() => {
                            setMappingModal(false);
                            setSelectedCategory(defCategory);
                        }}
                        color="secondary"
                        variant="contained"
                    >
                        Cancel
                    </Button>

                    <Button onClick={handleMapping} color="primary" variant="contained">
                        {mapping ? <CircularProgress size={15} color="inherit" /> : 'Save Mapping'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default Categories;
